import React, { Component, createRef } from 'react';
import SVG from 'react-inlinesvg';
import classNames from 'classnames/bind';
import { Formik, Form } from 'formik';
import { find, findIndex, forOwn, some, isEqual, map, keys } from 'lodash';
import TextField from '@material-ui/core/TextField';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import Checkbox from '@material-ui/core/Checkbox';
import Divider from '@material-ui/core/Divider';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Input from '@material-ui/core/Input';
import CardTitle from '../../../shared/components/CardTitle/CardTitle';
import expressConfigurationSchema from '../../schemas/expressConfigurationSchema';
import styles from './expressConfiguration.module.scss';

const conditionalStyles = classNames.bind(styles);

export default class ExpressConfiguration extends Component {
    constructor(props) {
        super(props);

        this.form = createRef();
    }

    componentDidMount() {
        this.props.getTelephonyRequest();

        if (this.props.match.params.id) {
            this.handleSubmit();
        }
    }

    toggleService(service) {
        const currentStateIndex = findIndex(service, state => state);
        let nextStateIndex = currentStateIndex === service.length - 1 ? 0 : currentStateIndex + 1;

        service[currentStateIndex] = false;
        service[nextStateIndex] = true;

        this.form.current.setValues({
            ...this.form.current.state.values,
            [service]: service
        });
    }

    enableSecurityPIN() {
        const services = [
            this.form.current.state.values.Local,
            this.form.current.state.values.National,
            this.form.current.state.values.International,
            this.form.current.state.values.InterPBX,
            this.form.current.state.values.Mobile,
            this.form.current.state.values.Special,
            this.form.current.state.values.Emergency
        ];

        const lockedService = some(services, value => isEqual(value, [false, false, true]));

        this.form.current.setValues({
            ...this.form.current.state.values,
            'EnableSecurityPIN': lockedService,
            'UseSecurityPin': false
        });
    }

    isLocalTelephonyProfile() {
        return this.form.current ? this.form.current.state.values.Profiles !== 'local' : false;
    }

    async handleChange(field, value) {
        await this.form.current.setFieldValue(field, value, false);

        const validForm = await this.form.current.validateForm();

        if (keys(validForm).length === 0) {
            this.handleSubmit();
        } else if (this.props.ready) {
            this.props.expressConfigurationReady(false);
        }
    }

    async handleTelephonyProfileChange(newValue) {
        if (newValue !== 'local') {
            const selectedCallServices = find(this.props.telephonyProfiles.profiles,
                profile => profile.ProfileId === newValue).CallServices;

            forOwn(selectedCallServices, (value, key) => {
                const serviceValue = [false, false, false];

                serviceValue[+value.Status] = true;

                this.form.current.setFieldValue(key, serviceValue, false);
            });
        }
        else if (this.props.edition.user && !this.props.edition.user.IdProfiles.length) {
            await this.form.current.setFieldValue(
                'Local', this.transformCallService(this.props.edition.user.CallServices.Local), false
            );

            await this.form.current.setFieldValue(
                'National', this.transformCallService(this.props.edition.user.CallServices.National), false
            );

            await this.form.current.setFieldValue(
                'International', this.transformCallService(this.props.edition.user.CallServices.International), false
            );

            await this.form.current.setFieldValue(
                'InterPBX', this.transformCallService(this.props.edition.user.CallServices.InterPBX), false
            );

            await this.form.current.setFieldValue(
                'Mobile', this.transformCallService(this.props.edition.user.CallServices.Mobile), false
            );

            await this.form.current.setFieldValue(
                'Special', this.transformCallService(this.props.edition.user.CallServices.Special), false
            );

            await this.form.current.setFieldValue(
                'Emergency', this.transformCallService(this.props.edition.user.CallServices.Emergency), false
            );
        } else {
            await this.form.current.setFieldValue('Local', [true, false, false], false);
            await this.form.current.setFieldValue('National', [true, false, false], false);
            await this.form.current.setFieldValue('International', [true, false, false], false);
            await this.form.current.setFieldValue('InterPBX', [true, false, false], false);
            await this.form.current.setFieldValue('Mobile', [true, false, false], false);
            await this.form.current.setFieldValue('Special', [true, false, false], false);
            await this.form.current.setFieldValue('Emergency', [true, false, false], false);
        }

        await this.handleChange('Profiles', newValue);

        this.enableSecurityPIN();
    }

    handleSubmit() {
        this.props.expressConfigurationReady(true, this.form.current.state.values);
    }

    transformCallService(serviceValue) {
        const serviceTransformedValue = [false, false, false];

        serviceTransformedValue[+serviceValue] = true;

        return serviceTransformedValue;
    }

    render() {
        const userTypes = [{
            key: 'PHONE',
            value: 'users:phone'
        }, {
            key: 'VIDEOPHONE',
            value: 'users:video_phone'
        }];

        return (
            <div className={styles.container}>
                <CardTitle
                    description="express_configuration_detail"
                    dictionary="users"
                    title="express_configuration"
                />
                <div className={styles.formContainer}>
                    <Formik
                        ref={this.form}
                        initialValues={{
                            Edition: this.props.edition.user !== null,
                            UserType: this.props.edition.user ? this.props.edition.user.UserType : 'PHONE',
                            Extension: this.props.edition.user ? this.props.edition.user.Extension : '',
                            ExtensionPassword: '',
                            ShowPassword: false,
                            RepeatedPassword: '',
                            ShowRepeatedPassword: false,
                            Profiles: this.props.edition.user && this.props.edition.user.IdProfiles.length
                                ? this.props.edition.user.IdProfiles[0]
                                : 'local',
                            Local: this.props.edition.user
                                ? this.transformCallService(this.props.edition.user.CallServices.Local)
                                : [true, false, false],
                            National: this.props.edition.user
                                ? this.transformCallService(this.props.edition.user.CallServices.National)
                                : [true, false, false],
                            International: this.props.edition.user
                                ? this.transformCallService(this.props.edition.user.CallServices.International)
                                : [true, false, false],
                            InterPBX: this.props.edition.user
                                ? this.transformCallService(this.props.edition.user.CallServices.InterPBX)
                                : [true, false, false],
                            Mobile: this.props.edition.user
                                ? this.transformCallService(this.props.edition.user.CallServices.Mobile)
                                : [true, false, false],
                            Special: this.props.edition.user
                                ? this.transformCallService(this.props.edition.user.CallServices.Special)
                                : [true, false, false],
                            Emergency: this.props.edition.user
                                ? this.transformCallService(this.props.edition.user.CallServices.Emergency)
                                : [true, false, false],
                            EnableSecurityPIN:
                                this.props.edition.user &&
                                (!!(+this.props.edition.user.UseSecurityPin) ||
                                    some(this.props.edition.user.CallServices, service => service === "2")),
                            ShowSecurityPIN: false,
                            UseSecurityPin: this.props.edition.user
                                ? !!(+this.props.edition.user.UseSecurityPin)
                                : false,
                            SecurityPin: this.props.edition.user ? this.props.edition.user.SecurityPin : '',
                            ExtensionOnWebEnabled: this.props.edition.user
                                ? !!(+this.props.edition.user.ExtensionOnWebEnabled)
                                : false,
                            ExtensionOnMobileEnabled: this.props.edition.user
                                ? !!(+this.props.edition.user.ExtensionOnMobileEnabled)
                                : false,
                            UseVoicemail: this.props.edition.user
                                ? !!(+this.props.edition.user.UseVoicemail)
                                : false,
                            Monitor: this.props.edition.user
                                ? !!(+this.props.edition.user.Monitor)
                                : false,
                            Protocol: 'SIP',
                            RecordingCapacity: this.props.edition.user
                                ? this.props.edition.user.RecordingCapacity || 10
                                : 10
                        }}
                        enableReinitialize={true}
                        validationSchema={expressConfigurationSchema}
                        onSubmit={() => this.handleSubmit()}
                        render={props => (
                            <Form>
                                <div className={styles.row}>
                                    <div className={styles.subRow}>
                                        <FormControl className={styles.field}>
                                            <InputLabel>{this.props.t('common:type')}</InputLabel>
                                            <Select
                                                disabled={this.props.isCreating}
                                                value={props.values.UserType}
                                                onChange={event => this.handleChange('UserType', event.target.value)}
                                                onBlur={props.handleBlur}
                                                input={<Input name="UserType" />}
                                            >
                                                {map(userTypes, type =>
                                                    <MenuItem key={type.key} value={type.key}>
                                                        {this.props.t(type.value)}
                                                    </MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                    </div>
                                    <div className={styles.subRow}>
                                        <TextField
                                            disabled={this.props.isCreating || this.props.edition.user !== null}
                                            label={this.props.t('users:extension')}
                                            type="number"
                                            name="Extension"
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">
                                                    <SVG cacheGetRequests className={styles.phoneIcon} src="./icons/phone.svg" />
                                                </InputAdornment>
                                            }}
                                            className={styles.field}
                                            value={props.values.Extension}
                                            onChange={event => this.handleChange('Extension', event.target.value)}
                                            onBlur={props.handleBlur}
                                            error={props.touched.Extension && !!props.errors.Extension}
                                            helperText={props.touched.Extension && !!props.errors.Extension
                                                ? <span>{this.props.t(`validations:${props.errors.Extension}`)}</span>
                                                : ''
                                            }
                                        />
                                    </div>
                                </div>
                                <div className={styles.row}>
                                    <div className={styles.subRow}>
                                        <TextField
                                            disabled={this.props.isCreating}
                                            type={props.values.ShowPassword ? 'text' : 'password'}
                                            label={props.values.Edition
                                                ? this.props.t('users:new_password')
                                                : this.props.t('common:PASSWORD')
                                            }
                                            name="ExtensionPassword"
                                            value={props.values.ExtensionPassword}
                                            className={styles.field}
                                            onChange={event => this.handleChange('ExtensionPassword', event.target.value)}
                                            onBlur={props.handleBlur}
                                            error={props.touched.ExtensionPassword && !!props.errors.ExtensionPassword}
                                            helperText={props.touched.ExtensionPassword && !!props.errors.ExtensionPassword
                                                ? <span>{this.props.t(`validations:${props.errors.ExtensionPassword}`)}</span>
                                                : ''
                                            }
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">
                                                    <div
                                                        className={styles.clickableIcon}
                                                        onClick={() => {
                                                            props.setValues({
                                                                ...props.values,
                                                                ShowPassword: !props.values.ShowPassword
                                                            });
                                                        }}
                                                    >
                                                        {
                                                            props.values.ShowPassword &&
                                                            <SVG
                                                                cacheGetRequests
                                                                className={styles.icon}
                                                                src="./icons/hide.svg"
                                                            />
                                                        }
                                                        {
                                                            !props.values.ShowPassword &&
                                                            <SVG
                                                                cacheGetRequests
                                                                className={styles.icon}
                                                                src="./icons/show.svg"
                                                            />
                                                        }
                                                    </div>
                                                </InputAdornment>
                                            }}
                                        />
                                    </div>
                                    <div className={styles.subRow}>
                                        <TextField
                                            disabled={this.props.isCreating}
                                            type={props.values.ShowRepeatedPassword ? 'text' : 'password'}
                                            label={this.props.t('common:REPEATED_PASSWORD')}
                                            name="RepeatedPassword"
                                            value={props.values.RepeatedPassword}
                                            className={styles.field}
                                            onChange={event => this.handleChange('RepeatedPassword', event.target.value)}
                                            onBlur={props.handleBlur}
                                            error={props.touched.RepeatedPassword && !!props.errors.RepeatedPassword}
                                            helperText={props.touched.RepeatedPassword && !!props.errors.RepeatedPassword
                                                ? <span>{this.props.t(`validations:${props.errors.RepeatedPassword}`)}</span>
                                                : ''
                                            }
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end">
                                                    <div
                                                        className={styles.clickableIcon}
                                                        onClick={() => {
                                                            props.setValues({
                                                                ...props.values,
                                                                ShowRepeatedPassword: !props.values.ShowRepeatedPassword
                                                            });
                                                        }}
                                                    >
                                                        {
                                                            props.values.ShowRepeatedPassword &&
                                                            <SVG
                                                                cacheGetRequests
                                                                className={styles.icon}
                                                                src="./icons/hide.svg"
                                                            />
                                                        }
                                                        {
                                                            !props.values.ShowRepeatedPassword &&
                                                            <SVG
                                                                cacheGetRequests
                                                                className={styles.icon}
                                                                src="./icons/show.svg"
                                                            />
                                                        }
                                                    </div>
                                                </InputAdornment>
                                            }}
                                        />
                                    </div>
                                </div>
                                <Divider className={styles.divider} />
                                <div className={styles.row}>
                                    <div className={styles.subRow}>
                                        <FormControl className={styles.field}>
                                            <InputLabel htmlFor="Profiles">
                                                {this.props.t('users:telephony_profile')}
                                            </InputLabel>
                                            <Select
                                                disabled={this.props.isCreating}
                                                value={props.values.Profiles}
                                                onChange={event => this.handleTelephonyProfileChange(event.target.value)}
                                                onBlur={props.handleBlur}
                                                input={<Input name="Profiles" id="Profiles" />}
                                            >
                                                <MenuItem key={'local'} value={'local'}>{this.props.t('users:local')}</MenuItem>
                                                {map(this.props.telephonyProfiles.profiles, profile =>
                                                    <MenuItem key={profile.ProfileId} value={profile.ProfileId}>
                                                        {profile.Description}
                                                    </MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                    </div>
                                    <div className={styles.subRow}>
                                        <div className={styles.servicesContainer}>
                                            <FormControlLabel
                                                label={this.props.t('users:local')}
                                                name="Local"
                                                disabled={this.isLocalTelephonyProfile() || this.props.isCreating}
                                                onChange={event => {
                                                    props.handleChange(event);

                                                    this.toggleService(props.values.Local);
                                                    this.enableSecurityPIN();
                                                    this.handleChange('Local', props.values.Local);
                                                }}
                                                onBlur={props.handleBlur}
                                                control={
                                                    <Checkbox
                                                        checked={props.values.Local[0]}
                                                        icon={
                                                            <SVG
                                                                cacheGetRequests
                                                                className={conditionalStyles({
                                                                    checkBoxIcon: true,
                                                                    disabledIcon: this.isLocalTelephonyProfile()
                                                                })}
                                                                src="./icons/cancel.svg"
                                                            />
                                                        }
                                                        indeterminate={props.values.Local[2]}
                                                        indeterminateIcon={
                                                            <SVG
                                                                cacheGetRequests
                                                                className={conditionalStyles({
                                                                    checkBoxIcon: true,
                                                                    disabledIcon: this.isLocalTelephonyProfile()
                                                                })}
                                                                src="./icons/lock.svg"
                                                            />
                                                        }
                                                    />
                                                }
                                            />
                                            <FormControlLabel
                                                label="NDD"
                                                name="National"
                                                disabled={this.isLocalTelephonyProfile() || this.props.isCreating}
                                                onChange={event => {
                                                    props.handleChange(event);

                                                    this.toggleService(props.values.National);
                                                    this.enableSecurityPIN();
                                                    this.handleChange('National', props.values.National);
                                                }}
                                                onBlur={props.handleBlur}
                                                control={
                                                    <Checkbox
                                                        checked={props.values.National[0]}
                                                        icon={
                                                            <SVG
                                                                cacheGetRequests
                                                                className={conditionalStyles({
                                                                    checkBoxIcon: true,
                                                                    disabledIcon: this.isLocalTelephonyProfile()
                                                                })}
                                                                src="./icons/cancel.svg"
                                                            />
                                                        }
                                                        indeterminate={props.values.National[2]}
                                                        indeterminateIcon={
                                                            <SVG
                                                                cacheGetRequests
                                                                className={conditionalStyles({
                                                                    checkBoxIcon: true,
                                                                    disabledIcon: this.isLocalTelephonyProfile()
                                                                })}
                                                                src="./icons/lock.svg"
                                                            />
                                                        }
                                                    />
                                                }
                                            />
                                            <FormControlLabel
                                                label="IDD"
                                                name="International"
                                                disabled={this.isLocalTelephonyProfile() || this.props.isCreating}
                                                onChange={event => {
                                                    props.handleChange(event);

                                                    this.toggleService(props.values.International);
                                                    this.enableSecurityPIN();
                                                    this.handleChange('International', props.values.International);
                                                }}
                                                onBlur={props.handleBlur}
                                                control={
                                                    <Checkbox
                                                        checked={props.values.International[0]}
                                                        icon={
                                                            <SVG
                                                                cacheGetRequests
                                                                className={conditionalStyles({
                                                                    checkBoxIcon: true,
                                                                    disabledIcon: this.isLocalTelephonyProfile()
                                                                })}
                                                                src="./icons/cancel.svg"
                                                            />
                                                        }
                                                        indeterminate={props.values.International[2]}
                                                        indeterminateIcon={
                                                            <SVG
                                                                cacheGetRequests
                                                                className={conditionalStyles({
                                                                    checkBoxIcon: true,
                                                                    disabledIcon: this.isLocalTelephonyProfile()
                                                                })}
                                                                src="./icons/lock.svg"
                                                            />
                                                        }
                                                    />
                                                }
                                            />
                                            <FormControlLabel
                                                label="InterPBX"
                                                name="InterPBX"
                                                disabled={this.isLocalTelephonyProfile() || this.props.isCreating}
                                                onChange={event => {
                                                    props.handleChange(event);

                                                    this.toggleService(props.values.InterPBX);
                                                    this.enableSecurityPIN();
                                                    this.handleChange('InterPBX', props.values.InterPBX);
                                                }}
                                                onBlur={props.handleBlur}
                                                control={
                                                    <Checkbox
                                                        checked={props.values.InterPBX[0]}
                                                        icon={
                                                            <SVG
                                                                cacheGetRequests
                                                                className={conditionalStyles({
                                                                    checkBoxIcon: true,
                                                                    disabledIcon: this.isLocalTelephonyProfile()
                                                                })}
                                                                src="./icons/cancel.svg"
                                                            />
                                                        }
                                                        indeterminate={props.values.InterPBX[2]}
                                                        indeterminateIcon={
                                                            <SVG
                                                                cacheGetRequests
                                                                className={conditionalStyles({
                                                                    checkBoxIcon: true,
                                                                    disabledIcon: this.isLocalTelephonyProfile()
                                                                })}
                                                                src="./icons/lock.svg"
                                                            />
                                                        }
                                                    />
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.row}>
                                    <div className={styles.subRow}>
                                        <div
                                            className={conditionalStyles({
                                                pinContainer: true,
                                                hiddenPinContainer:
                                                    !props.values.EnableSecurityPIN &&
                                                    props.values.Profiles === 'local'
                                            })}
                                        >
                                            <FormControlLabel
                                                className={styles.pin}
                                                disabled={this.props.isCreating}
                                                control={<Checkbox checked={props.values.UseSecurityPin} />}
                                                label={this.props.t('users:security_pin')}
                                                name="UseSecurityPin"
                                                onChange={() => this.handleChange('UseSecurityPin', !props.values.UseSecurityPin)}
                                                onBlur={props.handleBlur}
                                            />
                                            <TextField
                                                disabled={this.props.isCreating}
                                                type={props.values.ShowSecurityPIN ? 'text' : 'password'}
                                                label={this.props.t('users:pin')}
                                                name="SecurityPin"
                                                value={props.values.SecurityPin}
                                                className={`${styles.field} ${styles.pin}`}
                                                onChange={event => this.handleChange('SecurityPin', event.target.value)}
                                                onBlur={props.handleBlur}
                                                error={props.touched.SecurityPin && !!props.errors.SecurityPin}
                                                helperText={props.touched.SecurityPin && !!props.errors.SecurityPin
                                                    ? <span>{this.props.t(`validations:${props.errors.SecurityPin}`)}</span>
                                                    : ''
                                                }
                                                InputProps={{
                                                    endAdornment: <InputAdornment position="end">
                                                        <div
                                                            className={styles.clickableIcon}
                                                            onClick={() => {
                                                                props.setValues({
                                                                    ...props.values,
                                                                    ShowSecurityPIN: !props.values.ShowSecurityPIN
                                                                });
                                                            }}
                                                        >
                                                            {
                                                                props.values.ShowSecurityPIN &&
                                                                <SVG
                                                                    cacheGetRequests
                                                                    className={styles.icon}
                                                                    src="./icons/hide.svg"
                                                                />
                                                            }
                                                            {
                                                                !props.values.ShowSecurityPIN &&
                                                                <SVG
                                                                    cacheGetRequests
                                                                    className={styles.icon}
                                                                    src="./icons/show.svg"
                                                                />
                                                            }
                                                        </div>
                                                    </InputAdornment>
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className={styles.subRow}>
                                        <div className={`${styles.servicesContainer} ${styles.smallServices}`}>
                                            <FormControlLabel
                                                label={this.props.t('users:mobile', { count: 2 })}
                                                name="Mobile"
                                                disabled={this.isLocalTelephonyProfile() || this.props.isCreating}
                                                onChange={event => {
                                                    props.handleChange(event);

                                                    this.toggleService(props.values.Mobile);
                                                    this.enableSecurityPIN();
                                                    this.handleChange('Mobile', props.values.Mobile);
                                                }}
                                                onBlur={props.handleBlur}
                                                control={
                                                    <Checkbox
                                                        checked={props.values.Mobile[0]}
                                                        icon={
                                                            <SVG
                                                                cacheGetRequests
                                                                className={conditionalStyles({
                                                                    checkBoxIcon: true,
                                                                    disabledIcon: this.isLocalTelephonyProfile()
                                                                })}
                                                                src="./icons/cancel.svg"
                                                            />
                                                        }
                                                        indeterminate={props.values.Mobile[2]}
                                                        indeterminateIcon={
                                                            <SVG
                                                                cacheGetRequests
                                                                className={conditionalStyles({
                                                                    checkBoxIcon: true,
                                                                    disabledIcon: this.isLocalTelephonyProfile()
                                                                })}
                                                                src="./icons/lock.svg"
                                                            />
                                                        }
                                                    />
                                                }
                                            />
                                            <FormControlLabel
                                                label={this.props.t('users:especial', { count: 2 })}
                                                name="Special"
                                                disabled={this.isLocalTelephonyProfile() || this.props.isCreating}
                                                onChange={event => {
                                                    props.handleChange(event);

                                                    this.toggleService(props.values.Special);
                                                    this.enableSecurityPIN();
                                                    this.handleChange('Special', props.values.Special);
                                                }}
                                                onBlur={props.handleBlur}
                                                control={
                                                    <Checkbox
                                                        checked={props.values.Special[0]}
                                                        icon={
                                                            <SVG
                                                                cacheGetRequests
                                                                className={conditionalStyles({
                                                                    checkBoxIcon: true,
                                                                    disabledIcon: this.isLocalTelephonyProfile()
                                                                })}
                                                                src="./icons/cancel.svg"
                                                            />
                                                        }
                                                        indeterminate={props.values.Special[2]}
                                                        indeterminateIcon={
                                                            <SVG
                                                                cacheGetRequests
                                                                className={conditionalStyles({
                                                                    checkBoxIcon: true,
                                                                    disabledIcon: this.isLocalTelephonyProfile()
                                                                })}
                                                                src="./icons/lock.svg"
                                                            />
                                                        }
                                                    />
                                                }
                                            />
                                            <FormControlLabel
                                                label={this.props.t('users:emergency', { count: 2 })}
                                                name="Emergency"
                                                disabled={this.isLocalTelephonyProfile() || this.props.isCreating}
                                                onChange={event => {
                                                    props.handleChange(event);

                                                    this.toggleService(props.values.Emergency);
                                                    this.enableSecurityPIN();
                                                    this.handleChange('Emergency', props.values.Emergency);
                                                }}
                                                onBlur={props.handleBlur}
                                                control={
                                                    <Checkbox
                                                        checked={props.values.Emergency[0]}
                                                        icon={
                                                            <SVG
                                                                cacheGetRequests
                                                                className={conditionalStyles({
                                                                    checkBoxIcon: true,
                                                                    disabledIcon: this.isLocalTelephonyProfile()
                                                                })}
                                                                src="./icons/cancel.svg"
                                                            />
                                                        }
                                                        indeterminate={props.values.Emergency[2]}
                                                        indeterminateIcon={
                                                            <SVG
                                                                cacheGetRequests
                                                                className={conditionalStyles({
                                                                    checkBoxIcon: true,
                                                                    disabledIcon: this.isLocalTelephonyProfile()
                                                                })}
                                                                src="./icons/lock.svg"
                                                            />
                                                        }
                                                    />
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                                <Divider className={styles.divider} />
                                <div className={styles.row}>
                                    <div className={styles.subRow}>
                                        <div className={styles.permissionsContainer}>
                                            <FormControlLabel
                                                disabled={this.props.isCreating}
                                                control={<Checkbox checked={props.values.ExtensionOnWebEnabled} />}
                                                label={this.props.t('users:desktop_extension')}
                                                name="ExtensionOnWebEnabled"
                                                onChange={() =>
                                                    this.handleChange('ExtensionOnWebEnabled', !props.values.ExtensionOnWebEnabled)
                                                }
                                                onBlur={props.handleBlur}
                                            />
                                            <FormControlLabel
                                                disabled={this.props.isCreating}
                                                control={<Checkbox checked={props.values.ExtensionOnMobileEnabled} />}
                                                label={this.props.t('users:mobile_extension')}
                                                name="ExtensionOnMobileEnabled"
                                                onChange={() =>
                                                    this.handleChange('ExtensionOnMobileEnabled', !props.values.ExtensionOnMobileEnabled)
                                                }
                                                onBlur={props.handleBlur}
                                            />
                                            <FormControlLabel
                                                disabled={this.props.isCreating}
                                                control={<Checkbox checked={props.values.UseVoicemail} />}
                                                label={this.props.t('common:voicemail')}
                                                name="UseVoicemail"
                                                onChange={() =>
                                                    this.handleChange('UseVoicemail', !props.values.UseVoicemail)
                                                }
                                                onBlur={props.handleBlur}
                                            />
                                            <FormControlLabel
                                                disabled={this.props.isCreating}
                                                control={<Checkbox checked={props.values.Monitor} />}
                                                label={this.props.t('common:recordings')}
                                                name="Monitor"
                                                onChange={() => this.handleChange('Monitor', !props.values.Monitor)}
                                                onBlur={props.handleBlur}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        )}
                    />
                </div>
            </div>
        );
    }
};
